import React, { useEffect, useMemo, useState } from "react";
import AppModal from "../components/common/AppModal";
import ReactGA from "react-ga4";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
    Box,
    Button,
    Divider,
    Flex,
    Grid,
    GridItem,
    Heading,
    Icon,
    IconButton,
    Link,
    Skeleton,
    SkeletonText,
    Stack,
    Text,
    Tooltip,
    useColorModeValue,
} from "@chakra-ui/react";
import {
    MdAccessTime,
    MdArrowBack,
    MdLanguage,
    MdLocationOn,
    MdPhone,
    MdStorefront,
    MdVerified,
} from "react-icons/md";
import { FaInstagram } from "react-icons/fa";
import { PiFlag, PiHeart, PiNavigationArrow } from "react-icons/pi";

import ReportShopModal from "../components/common/ReportShopModal";
import ShopPhoto from "../components/common/ShopPhoto";
import ShareOptions from "../components/common/ShareOptions";
import { AttributeCategory, CoffeeEntryCard, useTk } from "../components/ShopProfileComponents";
import { shopShareUrl } from "../constants";
import { ALL_TAGS, CATEGORY_META } from "../lib/shopTags";
import { isOpenNow } from "../lib/ownerHours";
import useResponsiveLayout from "../hooks/useResponsiveLayout";
import { VERIFIED_BLUE } from "../styles/theme";
import { shopPhotoUrl } from "../lib/shopPhotoUrl";

// ─── Constants ─────────────────────────────────────────────────────────────────

const LOADING_PHRASES = [
  "Reading the bean list...",
  "Tasting the notes...",
  "Sourcing origin data...",
  "Analysing roast profiles...",
  "Cross-referencing regions...",
  "AI is writing the bio...",
  "Looking up the roaster...",
  "AI is tagging the vibe...",
  "AI is identifying the beans...",
  "Mapping the origins...",
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ShopProfilePage({ asPanel = false }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Overlay may render outside a /shop/:id Route match — fall back to the URL.
  const id = params.id || location.pathname.match(/^\/shop\/([^/]+)/)?.[1];
  const tk = useTk();
  const { isDesktopLayout, isCompactHeight, isPhoneLandscape, isNarrowPhone } = useResponsiveLayout();
  const goBack = () => (location.state?.shop || location.state?.backgroundLocation ? navigate(-1) : navigate("/explore"));
  // Light: tinted outline CTA (solid brown brick fights the cream page). Dark: filled accent.
  const primaryBg = useColorModeValue("rgba(139,62,18,0.09)", tk.accent);
  const primaryColor = useColorModeValue(tk.accent, "#1a1209");
  const primaryBorder = useColorModeValue("1.5px solid rgba(139,62,18,0.55)", "1px solid transparent");
  const primaryHover = useColorModeValue(
    { bg: "rgba(139,62,18,0.15)" },
    { opacity: 0.88, bg: tk.accent },
  );
  const secondaryBg = useColorModeValue("#ffffff", tk.surface);
  const googleAttrColor = useColorModeValue("#5E5E5E", "#FFFFFF");
  const secondaryBorder = useColorModeValue("1px solid rgba(0,0,0,0.12)", `1px solid ${tk.border}`);
  const secondaryHover = useColorModeValue(
    { bg: "rgba(139,62,18,0.05)" },
    { bg: tk.surface2 },
  );

  const [baseShop, setShop] = useState(location.state?.shop || null);
  const [shopLoading, setShopLoading] = useState(!location.state?.shop);
  const [shopError, setShopError] = useState(null); // null | "not_found" | "rate_limited" | "error"
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [appModalContext, setAppModalContext] = useState(null);

  const [loadingPhrase, setLoadingPhrase] = useState(LOADING_PHRASES[0]);
  useEffect(() => {
    if (!profileLoading) return;
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % LOADING_PHRASES.length;
      setLoadingPhrase(LOADING_PHRASES[i]);
    }, 1800);
    return () => clearInterval(interval);
  }, [profileLoading]);

  const API_BASE = process.env.REACT_APP_SERVER_BASE_URL?.replace(/\/$/, "") || "http://localhost:3001";
  const today = new Date().toLocaleDateString("en-US", { weekday: "long" });

  useEffect(() => {
    if (!baseShop) return;
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: baseShop.name });
  }, [baseShop]);

  // Fallback: fetch basic shop data when page is loaded via direct URL (no router state)
  useEffect(() => {
    if (baseShop || !id) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/shops/${id}`);
        if (res.status === 410) return navigate("/not-listed", { replace: true });
        if (!res.ok) throw new Error(`${res.status}`);
        setShop(await res.json());
      } catch (err) {
        // Distinguish a genuine 404 from a transient failure. Previously every failure
        // fell through to "Shop not found", so a rate-limited or failing request told
        // someone their shared link pointed at a shop that no longer exists.
        setShopError(
          err.message === "404" ? "not_found"
          : err.message === "429" ? "rate_limited"
          : "error",
        );
      } finally {
        setShopLoading(false);
      }
    })();
  }, [id, baseShop, API_BASE, navigate]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/shops/${id}/profile`,
        );
        if (res.status === 410) return navigate("/not-listed", { replace: true });
        if (!res.ok) throw new Error(`${res.status}`);
        setProfile(await res.json());
      } catch (err) {
        setProfileError(err.message === "429" ? "rate_limited" : true);
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [id, API_BASE, navigate]);

  // What the owner told us wins over Google's live values — owner data is always what shows.
  const owner = profile?.owner;
  const shop = useMemo(() => baseShop && {
    ...baseShop,
    name: owner?.name || baseShop.name,
    address: owner?.address || baseShop.address,
    // Owner hours decide open/closed too, so the pill can't contradict the hours listed below it.
    open_now: owner?.hours ? isOpenNow(owner.hours) : baseShop.open_now,
    opening_hours: owner?.hours || baseShop.opening_hours,
    // The server only stores http(s) owner links; checked again here because this reaches an href.
    website: (/^https?:\/\//i.test(owner?.website || "") && owner.website) || baseShop.website,
  }, [baseShop, owner]);

  useEffect(() => {
    if (shop?.name) document.title = `${shop.name} · Kuthé`;
  }, [shop?.name]);

  const handleDirections = () => {
    ReactGA.event({ category: "Button", action: "Click", label: `Get Directions - ${shop.name}` });
    window.open(
      owner?.address
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${shop.name}, ${owner.address}`)}`
        : shop.google_maps_url || `https://maps.google.com/?q=${shop.lat},${shop.lng}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  if (!shop)
    return (
      <Flex minH="80vh" direction="column" align="center" justify="center" gap={4}>
        {shopLoading
          ? <Text color={tk.creamDim}>Loading...</Text>
          : shopError === "rate_limited" || shopError === "error"
            ? <>
                <Text color={tk.creamDim}>
                  {shopError === "rate_limited"
                    ? "Too many requests just now - give it a moment."
                    : "Couldn't load this shop. Check your connection."}
                </Text>
                <Button onClick={() => window.location.reload()}>Try again</Button>
              </>
            : <>
                <Text color={tk.creamDim}>Shop not found.</Text>
                <Button onClick={() => navigate("/explore")}>Back to explore</Button>
              </>}
      </Flex>
    );

  const imgUrl = shopPhotoUrl(shop, 1200);

  // Group tags by category — all tags shown, confirmed ones highlighted
  const confirmedTagSet = new Set(profile?.tags || []);
  const ownerTagSet = new Set(profile?.ownerTags || []);
  const isSmallChain = profile?.isSmallChain ?? false;
  const groups = Object.entries(ALL_TAGS).map(([category, allTags]) => {
    let tags = allTags.map((tag) => ({ ...tag, confirmed: confirmedTagSet.has(tag.key), ownerVerified: ownerTagSet.has(tag.key) }));
    if (category === 'place') {
      tags = tags.filter((t) => t.key !== 'single_location');
      if (isSmallChain) {
        tags.unshift({ key: 'small_chain', label: 'Small chain', confirmed: true });
      }
    }
    return { key: category, tags };
  });

  // Web: Directions is the real next step. Save opens the iOS app modal; Share uses the themed menu.
  const btnSecondary = {
    w: "100%",
    minW: 0,
    h: isDesktopLayout ? "40px" : "44px",
    borderRadius: "full",
    fontSize: isDesktopLayout ? "12px" : "11px",
    lineHeight: 1,
    fontWeight: 500,
    px: isDesktopLayout ? 4 : 2,
    bg: secondaryBg,
    color: tk.cream,
    border: secondaryBorder,
    _hover: secondaryHover,
  };
  const btnPrimary = {
    ...btnSecondary,
    bg: primaryBg,
    color: primaryColor,
    border: primaryBorder,
    fontWeight: 600,
    _hover: primaryHover,
  };

  const sectionPx = asPanel ? 5 : isDesktopLayout ? { md: 8, lg: 10 } : { base: 4, sm: 5 };

  const hero = (
    <Box
      position="relative"
      w="100%"
      maxW="100%"
      h={
        asPanel
          ? "220px"
          : isPhoneLandscape
            ? "150px"
            : isCompactHeight
              ? "190px"
              : isDesktopLayout
                ? { md: "300px", lg: "360px", xl: "440px", "2xl": "520px" }
                : "260px"
      }
      overflow="hidden"
      flexShrink={0}
      alignSelf="stretch"
    >
      <ShopPhoto
        src={imgUrl}
        alt={shop.name}
        iconSize="48px"
        position="absolute"
        inset={0}
        w="100%"
        h="100%"
        maxW="none"
        objectFit="cover"
        objectPosition="center"
        filter={imgUrl ? "brightness(0.75)" : undefined}
      />
      <Box position="absolute" inset={0} bgGradient={asPanel ? {
        base: `linear(to-b, transparent 0%, transparent 55%, ${tk.fadeBg} 100%)`,
      } : {
        base: `linear(to-b, ${tk.fadeBg} 0%, transparent 10%, transparent 70%, ${tk.fadeBg} 100%)`,
        lg: `linear(to-b, transparent 0%, transparent 70%, ${tk.fadeBg} 100%)`,
      }} />
      <IconButton icon={<MdArrowBack size={18} />} onClick={goBack} position="absolute" top={4} left={4} w="44px" h="44px" minW="44px" borderRadius="full" bg="rgba(0,0,0,0.55)" color="white" border="none" backdropFilter="blur(10px)" _hover={{ bg: "rgba(0,0,0,0.75)" }} aria-label="Go back"/>
      <Tooltip label="Report an issue" hasArrow placement="top" openDelay={400}>
        <IconButton
          icon={<Flex w="30px" h="30px" borderRadius="full" bg="rgba(0,0,0,0.4)" color="rgba(255,255,255,0.7)" align="center" justify="center" backdropFilter="blur(8px)"><PiFlag size="16px" /></Flex>}
          onClick={() => setReportOpen(true)} position="absolute" bottom={2} right={2}
          w="44px" h="44px" minW="44px" borderRadius="full" bg="transparent" color="white"
          border="none" _hover={{ color: "white" }} aria-label="Report an issue"
        />
      </Tooltip>
      {/* Google requires the photo author's name, linked, on every Places photo. No size rule. */}
      {imgUrl && shop.photo_attribution && (
        <Link
          href={shop.photo_attribution.uri || undefined}
          isExternal
          position="absolute"
          bottom={4}
          left={4}
          maxW="55%"
          noOfLines={1}
          fontSize="10px"
          lineHeight="14px"
          px="6px"
          borderRadius="full"
          bg="rgba(0,0,0,0.4)"
          color="rgba(255,255,255,0.85)"
          backdropFilter="blur(8px)"
          _hover={{ color: "white" }}
          onClick={(e) => e.stopPropagation()}
        >
          Photo: {shop.photo_attribution.name}
        </Link>
      )}
      {shop.open_now != null && (
        <Flex
          position="absolute"
          top={4}
          right={4}
          align="center"
          gap="6px"
          bg="rgba(0,0,0,0.52)"
          backdropFilter="blur(8px)"
          borderRadius="full"
          px="10px"
          py="5px"
        >
          <Box
            w="6px"
            h="6px"
            borderRadius="full"
            bg={shop.open_now ? tk.open : tk.closed}
            flexShrink={0}
          />
          <Text fontSize="12px" fontWeight={500} color="white" letterSpacing="0.08em" textTransform="uppercase">
            {shop.open_now ? "Open Now" : "Closed"}
          </Text>
        </Flex>
      )}
    </Box>
  );

  const identity = (
    <Box px={sectionPx} mt={-3} position="relative" zIndex={2} mb={asPanel ? 4 : 5}>
      <Heading
        fontFamily="heading"
        fontSize={asPanel ? "24px" : isDesktopLayout ? { md: "28px", lg: "34px", xl: "40px", "2xl": "46px" } : "22px"}
        fontWeight={400}
        color={tk.cream}
        mb={2}
      >
        {shop.name}
      </Heading>
      <Flex align="center" wrap="wrap" gap={2} fontSize="12px">
        {profile?.ownerVerified && (
          <>
            <Flex align="center" gap="3px" color={tk.cream} fontWeight={500}><Icon as={MdVerified} boxSize="14px" color={VERIFIED_BLUE} />Owner verified</Flex>
            {(shop.rating || shop.address) && <Box w="3px" h="3px" borderRadius="full" bg={tk.creamDim} />}
          </>
        )}
        {shop.rating && (
          <Flex align="center" gap={1} color={tk.gold} fontWeight={500}>
            <Text>★ {shop.rating}</Text>
            <Text color={tk.creamDim} fontWeight={300}>
              ({shop.user_ratings_total?.toLocaleString()} ratings)
            </Text>
          </Flex>
        )}
        {shop.address && (
          <>
            <Box w="3px" h="3px" borderRadius="full" bg={tk.creamDim} />
            <Text color={tk.creamDim}>
              {shop.address.split(",").slice(1, 3).join(",").trim()}
            </Text>
          </>
        )}
      </Flex>
    </Box>
  );

  const actions = (
    <Grid
      templateColumns={isNarrowPhone ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))"}
      gap={isDesktopLayout ? 3 : 2}
      px={sectionPx}
      mb={asPanel ? 4 : 7}
    >
      <Button
        {...btnPrimary}
        gridColumn={isNarrowPhone ? "1 / -1" : "auto"}
        onClick={handleDirections}
      >
        <Flex align="center" justify="center" gap="8px">
          <Flex w="17px" h="17px" align="center" justify="center" flexShrink={0}>
            <PiNavigationArrow size="17px" aria-hidden />
          </Flex>
          <Text as="span" lineHeight="1">Directions</Text>
        </Flex>
      </Button>
      <Button
        {...btnSecondary}
        onClick={() => {
          ReactGA.event({ category: "App", action: "Save Intent", label: shop.name });
          setAppModalContext("save");
        }}
      >
        <Flex align="center" justify="center" gap="8px">
          <Flex w="17px" h="17px" align="center" justify="center" flexShrink={0}>
            <PiHeart size="17px" aria-hidden />
          </Flex>
          <Text as="span" lineHeight="1">Save in app</Text>
        </Flex>
      </Button>
      <ShareOptions
        url={shopShareUrl(shop.uuid || id)}
        name={shop.name}
        fullWidth
        buttonProps={btnSecondary}
      />
    </Grid>
  );

  const belowPills = (
    <>
      <Divider borderColor={tk.border} mx={sectionPx} w="auto" mb={7} />

      <Box px={sectionPx} mb={7}>
        <Text fontSize="12px" fontWeight={500} letterSpacing="0.14em" textTransform="uppercase" color={tk.creamDim} mb={3}>
          The Details
        </Text>
        <Flex align="flex-start" gap={3} py="9px" borderBottom={`1px solid ${tk.border}`}>
          <Box color={tk.accent} mt="2px" flexShrink={0}>
            <MdLocationOn size={15} />
          </Box>
          <Text fontSize="14px" color={tk.creamDim}>
            {shop.address}
          </Text>
        </Flex>
        {shop.opening_hours && (
          <Flex align="flex-start" gap={3} py="9px">
            <Box color={tk.accent} mt="2px" flexShrink={0}>
              <MdAccessTime size={15} />
            </Box>
            <Grid templateColumns={{ base: "80px 1fr", md: "90px 1fr" }} gap="3px 0" fontSize="14px" w="100%">
              {shop.opening_hours.map((line, i) => {
                const colonIdx = line.indexOf(":");
                const day = line.substring(0, colonIdx);
                const time = line.substring(colonIdx + 1).trim();
                const isToday = day.toLowerCase() === today.toLowerCase();
                return (
                  <React.Fragment key={i}>
                    <GridItem color={isToday ? tk.accent : tk.creamDim} fontWeight={isToday ? 500 : 300}>
                      {day}
                    </GridItem>
                    <GridItem color={isToday ? tk.accent : tk.cream} fontWeight={isToday ? 500 : 300}>
                      {time}
                    </GridItem>
                  </React.Fragment>
                );
              })}
            </Grid>
          </Flex>
        )}
        {owner?.phone && (
          <Flex align="center" gap={3} py="9px" borderTop={`1px solid ${tk.border}`}>
            <Box color={tk.accent} flexShrink={0}><MdPhone size={15} /></Box>
            <Link href={`tel:${owner.phone.replace(/[^\d+]/g, "")}`} fontSize="14px" color={tk.creamDim}>{owner.phone}</Link>
          </Flex>
        )}
        {owner?.yearFounded && (
          <Flex align="center" gap={3} py="9px" borderTop={`1px solid ${tk.border}`}>
            <Box color={tk.accent} flexShrink={0}><MdStorefront size={15} /></Box>
            <Text fontSize="14px" color={tk.creamDim}>Open since {owner.yearFounded}</Text>
          </Flex>
        )}
      </Box>


      {/* ── Bio ── */}
      <Box px={sectionPx} mb={4}>
        <Box maxW={{ "2xl": "780px" }}>
          {profileLoading ? (
            <Flex direction="column" align="center" mb={5} gap={3}>
              <Box
                color={tk.accent}
                sx={{
                  '@keyframes brewFloat': {
                    '0%, 100%': { transform: 'translateY(0px)' },
                    '50%': { transform: 'translateY(-7px)' },
                  },
                  '@keyframes sp1': { '0%': { opacity: 0.15 }, '20%': { opacity: 1 }, '40%, 100%': { opacity: 0.15 } },
                  '@keyframes sp2': { '0%, 25%': { opacity: 0.15 }, '50%': { opacity: 1 }, '70%, 100%': { opacity: 0.15 } },
                  '@keyframes sp3': { '0%, 55%': { opacity: 0.15 }, '78%': { opacity: 1 }, '95%, 100%': { opacity: 0.15 } },
                  animation: 'brewFloat 2.6s ease-in-out infinite',
                }}
              >
                <svg width="72" height="72" viewBox="0 0 120 110" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 48 L22 85 Q22 92 60 92 Q98 92 98 85 L102 48 Z"/>
                  <path d="M97 60 Q119 60 119 72 Q119 84 97 82" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round"/>
                  <rect x="8" y="94" width="104" height="8" rx="4"/>
                  <path style={{ animation: 'sp1 2.4s ease-in-out infinite' }} d="M73 13 L76 23 L86 26 L76 29 L73 39 L70 29 L60 26 L70 23 Z"/>
                  <path style={{ animation: 'sp2 2.4s ease-in-out infinite' }} d="M43 11 L45 18 L52 20 L45 22 L43 29 L41 22 L34 20 L41 18 Z"/>
                  <path style={{ animation: 'sp3 2.4s ease-in-out infinite' }} d="M63 3 L64.5 7.5 L69 9 L64.5 10.5 L63 15 L61.5 10.5 L57 9 L61.5 7.5 Z"/>
                </svg>
              </Box>
              <Text fontSize="11px" color={tk.accent} fontStyle="italic" opacity={0.85} textAlign="center">{loadingPhrase}</Text>
            </Flex>
          ) : (
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontSize="12px" fontWeight={500} letterSpacing="0.14em" textTransform="uppercase" color={tk.creamDim}>About</Text>
              {profile?.bio && <Text fontSize="12px" color={tk.creamDim}>{profile.bioSource === "owner" ? "From the owner" : "AI summary"}</Text>}
            </Flex>
          )}
          {profileLoading ? (
            <SkeletonText noOfLines={3} spacing={3} skeletonHeight="14px" startColor={tk.surface} endColor={tk.surface2} />
          ) : profileError ? (
            <Text fontSize="14px" color={tk.creamDim} fontStyle="italic">
              {profileError === "rate_limited"
                ? "Too many requests - please wait a moment and try again."
                : "Profile not available for this shop yet."}
            </Text>
          ) : profile?.bio ? (
            <Box borderLeft={`2px solid ${tk.accent}`} pl={4}>
              <Text fontSize="14px" color={tk.creamDim} lineHeight={1.75} fontStyle="italic">
                {profile.bio}
              </Text>
            </Box>
          ) : (
            <Text fontSize="14px" color={tk.creamDim}>
              We don't have a summary for {shop.name} yet - there wasn't enough out there to write one.
            </Text>
          )}
          {owner?.notes && (
            <Text fontSize="14px" color={tk.creamDim} lineHeight={1.75} mt={4} whiteSpace="pre-wrap">
              {owner.notes}
            </Text>
          )}
        </Box>
      </Box>

      {(shop.website || profile?.instagramUrl) && (
        <Flex px={sectionPx} gap={4} mb={7} mt={5} wrap="wrap">
          {shop.website && (
            <Flex align="center" gap={1}>
              <Box color={tk.accent}><MdLanguage size={14} /></Box>
              <Link href={shop.website} isExternal fontSize="12px" color={tk.accent} _hover={{ textDecoration: "underline" }} onClick={(e) => { e.stopPropagation(); ReactGA.event({ category: "Link", action: "Click", label: `Website - ${shop.name}` }); }}>
                {(() => { try { return new URL(shop.website).hostname.replace(/^www\./, ""); } catch { return shop.website; } })()}
              </Link>
            </Flex>
          )}
          {profile?.instagramUrl && (
            <Flex align="center" gap={1}>
              <Box color={tk.accent}><FaInstagram size={14} /></Box>
              <Link href={profile.instagramUrl} isExternal fontSize="12px" color={tk.accent} _hover={{ textDecoration: "underline" }} onClick={(e) => { e.stopPropagation(); ReactGA.event({ category: "Link", action: "Click", label: `Instagram - ${shop.name}` }); }}>
                {profile.instagramUrl.replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, "")}
              </Link>
            </Flex>
          )}
        </Flex>
      )}

      {profile?.collections?.length > 0 && (
        <Box px={sectionPx} mb={7}>
          <Text fontSize="12px" fontWeight={500} letterSpacing="0.14em" textTransform="uppercase" color={tk.creamDim} mb={3}>
            Featured in
          </Text>
          <Flex gap={2} wrap="wrap">
            {profile.collections.map((c) => (
              <Link
                key={c.slug}
                href={`/collections/${c.slug}`}
                onClick={(e) => { e.preventDefault(); navigate(`/collections/${c.slug}`); }}
                fontSize="13px" color={tk.cream} bg={tk.surface} border="1px solid" borderColor={tk.border}
                borderRadius="full" px={3} py={1} _hover={{ borderColor: tk.accent, textDecoration: "none" }}
              >
                {c.title}
              </Link>
            ))}
          </Flex>
        </Box>
      )}

      {(profileLoading || !profileError) && (
        <Box display={asPanel ? "block" : { base: "block", lg: "none" }} px={sectionPx} mb={7}>
          <Text fontSize="12px" fontWeight={500} letterSpacing="0.14em" textTransform="uppercase" color={tk.creamDim} mb={3}>
            What to expect
          </Text>
          {profileLoading ? (
            <Stack spacing={3}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} h="48px" borderRadius="12px" startColor={tk.surface} endColor={tk.surface2} />
              ))}
            </Stack>
          ) : (
            <Stack spacing={3}>
              {groups.map((group) => (
                <AttributeCategory key={group.key} title={CATEGORY_META[group.key].title} emoji={CATEGORY_META[group.key].emoji} tags={group.tags} defaultOpen={false} />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {!profileLoading && !profileError && profile?.coffeeBeans?.length > 0 && (
        <Box px={sectionPx} mb={7}>
          <Divider borderColor={tk.border} w="auto" mb={7} />
          <Text fontSize="12px" fontWeight={500} letterSpacing="0.14em" textTransform="uppercase" color={tk.creamDim} mb={4}>
            The beans
          </Text>
          <Grid templateColumns={asPanel ? "1fr" : { base: "1fr", lg: "1fr 1fr" }} gap={6}>
            {profile.coffeeBeans.map((bean, i) => <CoffeeEntryCard key={i} item={bean} />)}
          </Grid>
        </Box>
      )}

      {(!profileError || shop.website || profile?.instagramUrl) && (
        <Divider borderColor={tk.border} mx={sectionPx} w="auto" mb={7} />
      )}

      {/* Places data on a view with no Google map needs this. Google fixes the text, 12–16px, weight
          400, and the colour (white, #1F1F1F or #5E5E5E) — hence the hex values outside tk. */}
      <Text px={sectionPx} mb={7} fontSize="12px" fontWeight={400} color={googleAttrColor} fontFamily="Roboto, sans-serif">
        Google Maps
      </Text>

      {/* No public entry to the owner flow yet — bag photos aren't persisted and
          transcription is off, so it isn't ready to hand to a stranger. The route
          still works: swap /shop for /owner in the URL while standing with them.
          Restore the "This is my shop" link here when the flow is finished. */}
    </>
  );

  const attributesSidebar = (profileLoading || !profileError) && (
    <Box display={asPanel ? "none" : { base: "none", lg: "block" }} position="sticky" top="20px" px={0} mb={7}>
      <Text fontSize="12px" fontWeight={500} letterSpacing="0.14em" textTransform="uppercase" color={tk.creamDim} mb={3}>
        What to expect
      </Text>
      {profileLoading ? (
        <Stack spacing={3}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} h="48px" borderRadius="12px" startColor={tk.surface} endColor={tk.surface2} />
          ))}
        </Stack>
      ) : (
        <Stack spacing={3}>
          {groups.map((group, i) => (
            <AttributeCategory key={group.key} title={CATEGORY_META[group.key].title} emoji={CATEGORY_META[group.key].emoji} tags={group.tags} defaultOpen={i === 0} />
          ))}
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      <Box
        bg={tk.fadeBg}
        color={tk.cream}
        fontFamily="body"
        fontSize="14px"
        minH={asPanel ? "100%" : "100vh"}
        h={asPanel ? "100%" : undefined}
        w="100%"
        maxW={asPanel ? "100%" : { base: "100%", lg: "1400px", "2xl": "1700px" }}
        mx="auto"
        display={asPanel ? "flex" : undefined}
        flexDirection={asPanel ? "column" : undefined}
        overflow={asPanel ? "hidden" : undefined}
        pb={asPanel ? 0 : isDesktopLayout ? 16 : "calc(64px + env(safe-area-inset-bottom, 0px))"}
        sx={asPanel ? undefined : { "@supports (min-height: 100dvh)": { minHeight: "100dvh" } }}
      >
        {asPanel ? (
          <>
            {/* Pinned: image → name → action pills */}
            <Box flexShrink={0} bg={tk.fadeBg} w="100%">
              {hero}
              {identity}
              {actions}
            </Box>
            {/* Scrollable: About and everything below */}
            <Box flex="1" minH={0} overflowY="auto" w="100%" pb={10}>
              {belowPills}
            </Box>
          </>
        ) : (
          <>
            {hero}
            <Grid
              templateColumns={{ base: "1fr", lg: "1fr 380px", xl: "1fr 440px", "2xl": "1fr 520px" }}
              gap={{ base: 0, lg: 8, xl: 12, "2xl": 16 }}
              px={{ base: 0, lg: 10, xl: 16, "2xl": 24 }}
              mt={{ base: 0, lg: 4 }}
              alignItems="start"
              w="100%"
            >
              <Box>
                {identity}
                {actions}
                {belowPills}
              </Box>
              {attributesSidebar}
            </Grid>
          </>
        )}
      </Box>

      <AppModal isOpen={!!appModalContext} onClose={() => setAppModalContext(null)} context={appModalContext || "general"} />

      <ReportShopModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        shopId={id}
        shopName={shop.name}
      />
    </>
  );
}
